import { publicEnv as env } from '@/lib/public-env'
import { defaultSiteContent } from '@/config/defaults'
import {
  siteContentOverrideSchema,
  siteContentSchema,
  type SiteContent,
  type SiteContentOverride,
} from '@/config/models'
import { environmentOverrides } from '@/config/environment-overrides'
import { resolveAppEnvironment } from '@/lib/deployment'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function mergeDeep<T>(target: T, ...sources: Array<SiteContentOverride | undefined>): T {
  const output = structuredClone(target)

  for (const source of sources) {
    if (!source) continue

    for (const [key, value] of Object.entries(source)) {
      const current = (output as Record<string, unknown>)[key]

      if (Array.isArray(value)) {
        ;(output as Record<string, unknown>)[key] = value
        continue
      }

      if (isPlainObject(current) && isPlainObject(value)) {
        ;(output as Record<string, unknown>)[key] = mergeDeep(current, value as SiteContentOverride)
        continue
      }

      if (value !== undefined) {
        ;(output as Record<string, unknown>)[key] = value
      }
    }
  }

  return output
}

function parseOverrideJSON(value: string | undefined): SiteContentOverride | undefined {
  if (!value) return undefined

  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    throw new Error('NEXT_PUBLIC_SITE_OVERRIDES must be valid JSON.')
  }

  const result = siteContentOverrideSchema.safeParse(parsed)
  if (!result.success) {
    const issues = result.error.issues.map(issue => `  - ${issue.path.join('.')}: ${issue.message}`).join('\n')
    throw new Error(`Invalid site override payload:\n${issues}`)
  }

  return result.data
}

const runtime = resolveAppEnvironment()

const resolved = mergeDeep(
  defaultSiteContent,
  environmentOverrides[runtime],
  parseOverrideJSON(env.NEXT_PUBLIC_SITE_OVERRIDES),
)

const parsed = siteContentSchema.safeParse(resolved)
if (!parsed.success) {
  const issues = parsed.error.issues.map(issue => `  - ${issue.path.join('.')}: ${issue.message}`).join('\n')
  throw new Error(`Invalid site content configuration:\n${issues}`)
}

export const siteContent: SiteContent = parsed.data
export type { SiteContent, SiteContentOverride } from '@/config/models'
